import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import Swal from 'sweetalert2';
import './SlotGame.css';
import SlotgameOpen from './assets/Slotgame-open.mp3';
import SlotTurn from './assets/slot-turn.mp3';
import Countdown3S from './assets/countdown-3S.wav';
// เพิ่ม import เสียงตอบถูก/ผิด
import TrueSound from './assets/true.wav';
import WrongSound from './assets/wrong.wav';
// เพิ่ม Firebase imports สำหรับการอัปเดตคะแนน
import { db } from '../Firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';

// สัญลักษณ์สำหรับแต่ละช่อง
const NUMBERS = ['1', '2', '3', '4', '5', '6', '7', '8', '9'];
const OPERATORS = ['+', '-', '×', '÷'];

// ฟังก์ชันสำหรับสร้าง Reel แบบสุ่ม
const generateReelSymbols = (finalSymbol, symbolType) => {
  const reel = [];
  const symbols = symbolType === 'number' ? NUMBERS : OPERATORS;
  
  // สร้างสัญลักษณ์แบบสุ่ม 30 ตัวเพื่อเป็นตัวหมุนผ่าน
  for (let i = 0; i < 30; i++) {
    reel.push(symbols[Math.floor(Math.random() * symbols.length)]);
  }
  // ใส่สัญลักษณ์ที่เป็นผลลัพธ์จริงไว้ตัวสุดท้าย
  reel.push(finalSymbol);
  return reel;
};

// ฟังก์ชันสำหรับสร้างตัวเลขที่เหมาะสมตาม operator
const generateAppropriateNumbers = (operator) => {
  if (operator === '-') {
    // สำหรับการลบ ให้ num2 น้อยกว่าหรือเท่ากับ num1
    const num1 = NUMBERS[Math.floor(Math.random() * NUMBERS.length)];
    const n1 = parseInt(num1);
    const availableNumbers = NUMBERS.filter(num => parseInt(num) <= n1);
    const num2 = availableNumbers[Math.floor(Math.random() * availableNumbers.length)];
    return [num1, num2];
  } else if (operator === '÷') {
    // สำหรับการหาร ให้ num1 หาร num2 แล้วได้ผลลัพธ์เป็นจำนวนเต็ม
    // สุ่ม num2 ก่อน (ตัวหาร)
    const num2 = NUMBERS[Math.floor(Math.random() * NUMBERS.length)];
    const n2 = parseInt(num2);
    // สุ่มผลลัพธ์ที่ต้องการ (ผลลัพธ์ต้องเป็น 1-9)
    const possibleResults = NUMBERS.map(n => parseInt(n));
    const result = possibleResults[Math.floor(Math.random() * possibleResults.length)];
    // num1 = num2 * result
    const num1 = String(n2 * result);
    // ถ้า num1 ไม่อยู่ใน NUMBERS ให้สุ่มใหม่ (recursion)
    if (!NUMBERS.includes(num1)) {
      return generateAppropriateNumbers(operator);
    }
    return [num1, num2];
  } else {
    // สำหรับ operator อื่นๆ ให้สุ่มตามปกติ
    const num1 = NUMBERS[Math.floor(Math.random() * NUMBERS.length)];
    const num2 = NUMBERS[Math.floor(Math.random() * NUMBERS.length)];
    return [num1, num2];
  }
};

// Component วงล้อ (Reel)
const Reel = React.memo(({ symbols, symbolType }) => (
  <div className="reel">
    {symbols.map((symbol, i) => (
      <div key={i} className="reel-symbol">
        <span className={`symbol-${symbolType}`}>{symbol}</span>
      </div>
    ))}
  </div>
));

// Component สำหรับนับถอยหลัง
const CountdownTimer = React.memo(({ countdown }) => (
  <div className="countdown">เวลาที่เหลือ: {countdown} วินาที</div>
));

// Component สำหรับจัดการ countdown
const CountdownManager = React.memo(({ 
  isActive, 
  onTimeUp, 
  onCountdownChange, 
  initialCountdown = 3 
}) => {
  const [countdown, setCountdown] = useState(initialCountdown);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (isActive && countdown > 0) {
      intervalRef.current = setInterval(() => {
        setCountdown(prev => {
          const newCount = prev - 1;
          onCountdownChange(newCount);
          if (newCount <= 0) {
            clearInterval(intervalRef.current);
            onTimeUp();
            return initialCountdown;
          }
          return newCount;
        });
      }, 1000);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isActive, onTimeUp, onCountdownChange, initialCountdown]);

  useEffect(() => {
    if (!isActive) {
      setCountdown(initialCountdown);
    }
  }, [isActive, initialCountdown]);

  return <CountdownTimer countdown={countdown} />;
});

// Component หลักของเกม
function SlotGame({ userData, onUserUpdate, onStopInformationSound }) {
  const [isSpinning, setIsSpinning] = useState(false);
  const [showAnswerInput, setShowAnswerInput] = useState(false);
  const [countdown, setCountdown] = useState(3);
  const [userAnswer, setUserAnswer] = useState('');
  const [currentResult, setCurrentResult] = useState(null);
  const [slotStopped, setSlotStopped] = useState(false);
  const [countdownTimer, setCountdownTimer] = useState(null);
  const [timeoutTimer, setTimeoutTimer] = useState(null);
  const [showResultModal, setShowResultModal] = useState(false);
  const [resultData, setResultData] = useState(null);
  const [finalReels, setFinalReels] = useState([
    ['1', '1', '1'], // ช่องที่ 1: หมายเลข
    ['+', '+', '+'], // ช่องที่ 2: สัญลักษณ์
    ['1', '1', '1'], // ช่องที่ 3: หมายเลข
  ]);
  
  const [reels, setReels] = useState([
    ['1', '1', '1'], // ช่องที่ 1: หมายเลข
    ['+', '+', '+'], // ช่องที่ 2: สัญลักษณ์
    ['1', '1', '1'], // ช่องที่ 3: หมายเลข
  ]);

  // เพิ่ม state
  const [answerDisabled, setAnswerDisabled] = useState(false);

  // เพิ่ม ref สำหรับ audio
  const slotOpenAudioRef = useRef(null);
  const slotTurnAudioRef = useRef(null);
  const countdownAudioRef = useRef(null);
  // เปลี่ยน ref สำหรับเสียงตอบถูก/ผิด ให้ใช้ Audio object
  const trueAudioRef = useRef(new Audio(TrueSound));
  const wrongAudioRef = useRef(new Audio(WrongSound));

  // ฟังก์ชันสำหรับอัปเดตคะแนนผู้ใช้
  const updateUserScore = useCallback(async (scoreToAdd) => {
    
    if (!userData?.username) {
      return;
    }

    try {
      const userRef = doc(db, "Competition-Training", userData.username);
      const docSnap = await getDoc(userRef);

      let currentDbScore = userData.score || 0;

      if (docSnap.exists()) {
        const dbData = docSnap.data();
        currentDbScore = dbData.score || 0;
      }

      const newScore = currentDbScore + scoreToAdd;

      await setDoc(userRef, { 
        score: newScore
      }, { merge: true });
      
      
      // แจ้ง App.js ให้รีเฟรชข้อมูลผู้ใช้
      if (onUserUpdate) {
        onUserUpdate(userData.username);
      }
    } catch (error) {
      console.error("Error updating user score in Firestore:", error);
      Swal.fire('เกิดข้อผิดพลาด', 'ไม่สามารถอัปเดตคะแนนได้', 'error');
    }
  }, [userData, onUserUpdate]);



  // ไม่เล่นเสียงทันทีเมื่อ component mount
  useEffect(() => {
    return () => {
      if (slotOpenAudioRef.current) {
        slotOpenAudioRef.current.pause();
        slotOpenAudioRef.current.currentTime = 0;
      }
    };
  }, []);

  const handlePlayAgain = useCallback(() => {
    // รีเซ็ตเกม
    setIsSpinning(false);
    setSlotStopped(false);
    setUserAnswer('');
    setCountdown(3);
    setShowAnswerInput(false);
    setShowResultModal(false);
    setResultData(null);
    setCurrentResult(null);
    setFinalReels([
      ['1', '1', '1'],
      ['+', '+', '+'],
      ['1', '1', '1'],
    ]);
    setReels([
      ['1', '1', '1'],
      ['+', '+', '+'],
      ['1', '1', '1'],
    ]);
  }, []);

  const handleClose = useCallback(() => {
    // ไปที่หน้า exercies
    window.location.href = '/exercies';
  }, []);

  // ปรับ checkAnswer ให้รับ argument permanent
  const checkAnswer = useCallback(async (currentResult) => {
    const [num1, operator, num2] = currentResult;
    let correctResult = 0;

    // คำนวณผลลัพธ์ที่ถูกต้อง
    const n1 = parseInt(num1);
    const n2 = parseInt(num2);
    
    switch (operator) {
      case '+':
        correctResult = n1 + n2;
        break;
      case '-':
        correctResult = n1 - n2;
        break;
      case '×':
        correctResult = n1 * n2;
        break;
      case '÷':
        correctResult = n1 / n2;
        break;
      default:
        correctResult = 0;
    }

    // ตรวจสอบคำตอบของผู้เล่น (ถ้าไม่มีคำตอบให้ถือว่าผิด)
    const userAnswerNum = parseInt(userAnswer) || 0;
    const isCorrect = userAnswerNum === correctResult;
    
    // คำนวณคะแนนที่ได้
    const scoreEarned = isCorrect ? 30 : 0;
    
    // เก็บข้อมูลผลลัพธ์
    const newResultData = {
      isCorrect,
      correctResult,
      userAnswer: userAnswerNum,
      scoreEarned
    };
    
    setResultData(newResultData);
    setShowResultModal(true);
    
    if (isCorrect) {
      // เล่นเสียงตอบถูก (reset ก่อนเล่น)
      if (trueAudioRef.current) {
        trueAudioRef.current.pause();
        trueAudioRef.current.currentTime = 0;
        trueAudioRef.current.play().catch(() => {});
      }
    } else {
      // เล่นเสียงตอบผิด (reset ก่อนเล่น)
      if (wrongAudioRef.current) {
        wrongAudioRef.current.pause();
        wrongAudioRef.current.currentTime = 0;
        wrongAudioRef.current.play().catch(() => {});
      }
    }
    
    // อัปเดตคะแนนในฐานข้อมูล
    await updateUserScore(scoreEarned);
    
    // แสดง SweetAlert ทันที
    Swal.fire({
      title: isCorrect ? 'ถูกต้อง! 🎉' : 'ผิด! 😔',
      text: isCorrect ? `คุณได้ ${scoreEarned} คะแนน!` : `คำตอบที่ถูกต้องคือ ${correctResult} (ได้ 0 คะแนน)`,
      icon: isCorrect ? 'success' : 'error',
      showCloseButton: false,
      showConfirmButton: true,
      confirmButtonText: 'OK',
      allowOutsideClick: false,
      allowEscapeKey: false,
    }); // <-- ไม่ต้องมี .then()
  }, [userAnswer, handlePlayAgain, handleClose, updateUserScore]);

  // ในฟังก์ชัน spin ให้แน่ใจว่าเสียงหยุดและ reset
  const spin = useCallback(() => {
    if (isSpinning) return;
    // หยุดเสียง information เมื่อกดปุ่มหมุน
    if (onStopInformationSound) {
      onStopInformationSound();
    }
    // เล่นเสียงหมุน
    if (slotTurnAudioRef.current) {
      slotTurnAudioRef.current.currentTime = 0;
      slotTurnAudioRef.current.play().catch(() => {});
    }

    // ยกเลิก timer เก่าถ้ามี
    if (countdownTimer) {
      clearInterval(countdownTimer);
    }
    if (timeoutTimer) {
      clearTimeout(timeoutTimer);
    }

    setIsSpinning(true);
    setSlotStopped(false);
    setUserAnswer('');
    setCountdown(3);
    setShowAnswerInput(true);
    setShowResultModal(false);
    setResultData(null);

    // สุ่ม operator ก่อน
    const operator = OPERATORS[Math.floor(Math.random() * OPERATORS.length)];
    
    // สร้างตัวเลขที่เหมาะสมตาม operator
    const [num1, num3] = generateAppropriateNumbers(operator);
    
    // สร้างผลลัพธ์สุดท้าย
    const finalResult = [
      num1, // ช่องที่ 1: หมายเลข
      operator, // ช่องที่ 2: สัญลักษณ์
      num3, // ช่องที่ 3: หมายเลข
    ];
    
    setCurrentResult(finalResult);
    
    // สร้าง Reel แบบสุ่มสำหรับแอนิเมชัน
    const newReels = [
      generateReelSymbols(finalResult[0], 'number'),
      generateReelSymbols(finalResult[1], 'operator'),
      generateReelSymbols(finalResult[2], 'number'),
    ];
    setReels(newReels);

    // จัดการหลังหมุนเสร็จ (6 วินาที)
    setTimeout(() => {
      setIsSpinning(false);
      setSlotStopped(true);
      // เก็บ reel สุดท้ายที่หยุดแล้ว
      setFinalReels(newReels);
      
      // เริ่มนับถอยหลังหลังจาก slot หยุดหมุน
      const interval = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(interval);
            setShowAnswerInput(false);
            // ตรวจสอบว่าผู้ใช้ยังไม่ได้ตอบคำถาม
            checkAnswer(finalResult).catch(console.error);
            return 3;
          }
          return prev - 1;
        });
      }, 1000);
      
      setCountdownTimer(interval);
      
      // หยุดนับถอยหลังหลังจาก 3 วินาที
      const timeout = setTimeout(() => {
        clearInterval(interval);
        setShowAnswerInput(false);
        // ตรวจสอบว่าผู้ใช้ยังไม่ได้ตอบคำถาม
        checkAnswer(finalResult).catch(console.error);
      }, 3000);
      
      setTimeoutTimer(timeout);

      // เล่นเสียง countdown ที่นี่ (เมื่อ timer เริ่มทำงาน)
      if (countdownAudioRef.current) {
        countdownAudioRef.current.currentTime = 0;
        countdownAudioRef.current.play().catch(() => {});
      }
    }, 4000); // เปลี่ยนเป็น 4 วินาที
    setAnswerDisabled(false); // เปิดให้กดคำตอบใหม่
  }, [isSpinning, countdownTimer, timeoutTimer, checkAnswer, onStopInformationSound]);

  // ปรับ handleSubmit ให้รับ argument permanent
  const handleSubmit = useCallback(async () => {
    if (currentResult) {
      // ยกเลิก timer เมื่อผู้ใช้ตอบคำถาม
      if (countdownTimer) {
        clearInterval(countdownTimer);
        setCountdownTimer(null);
      }
      if (timeoutTimer) {
        clearTimeout(timeoutTimer);
        setTimeoutTimer(null);
      }
      
      // เรียก checkAnswer โดยตรง
      const [num1, operator, num2] = currentResult;
      let correctResult = 0;

      // คำนวณผลลัพธ์ที่ถูกต้อง
      const n1 = parseInt(num1);
      const n2 = parseInt(num2);
      
      switch (operator) {
        case '+':
          correctResult = n1 + n2;
          break;
        case '-':
          correctResult = n1 - n2;
          break;
        case '×':
          correctResult = n1 * n2;
          break;
        case '÷':
          correctResult = n1 / n2;
          break;
        default:
          correctResult = 0;
      }

      // ตรวจสอบคำตอบของผู้เล่น
      const userAnswerNum = parseInt(userAnswer);
      const isCorrect = userAnswerNum === correctResult;
      
      // คำนวณคะแนนที่ได้
      const scoreEarned = isCorrect ? 30 : 0;
      
      // เก็บข้อมูลผลลัพธ์
      const newResultData = {
        isCorrect,
        correctResult,
        userAnswer: userAnswerNum,
        scoreEarned
      };
      
      setResultData(newResultData);
      setShowResultModal(true);
      setAnswerDisabled(true); // disable input หลังส่งคำตอบ
      
      if (isCorrect) {
        // เล่นเสียงตอบถูก (reset ก่อนเล่น)
        if (trueAudioRef.current) {
          trueAudioRef.current.pause();
          trueAudioRef.current.currentTime = 0;
          trueAudioRef.current.play().catch(() => {});
        }
      } else {
        // เล่นเสียงตอบผิด (reset ก่อนเล่น)
        if (wrongAudioRef.current) {
          wrongAudioRef.current.pause();
          wrongAudioRef.current.currentTime = 0;
          wrongAudioRef.current.play().catch(() => {});
        }
      }
      
      // อัปเดตคะแนนในฐานข้อมูล
      await updateUserScore(scoreEarned);
      
      // แสดง SweetAlert ทันที
      Swal.fire({
        title: isCorrect ? 'ถูกต้อง! 🎉' : 'ผิด! 😔',
        text: isCorrect ? `คุณได้ ${scoreEarned} คะแนน!` : `คำตอบที่ถูกต้องคือ ${correctResult} (ได้ 0 คะแนน)`,
        icon: isCorrect ? 'success' : 'error',
        showCloseButton: false,
        showConfirmButton: true,
        confirmButtonText: 'OK',
        allowOutsideClick: false,
        allowEscapeKey: false
      }); // <-- ไม่มี .then()
    }
  }, [currentResult, countdownTimer, timeoutTimer, userAnswer, handlePlayAgain, handleClose, checkAnswer, updateUserScore]);

  // ใช้ reel ที่เหมาะสม - ถ้า slot หยุดแล้วใช้ finalReels
  const displayReels = slotStopped ? finalReels : reels;

  return (
    <div className="slot-machine">
      <header style={{ textAlign: 'center', marginBottom: '10px' }}>
        <img
          src={process.env.PUBLIC_URL + '/Wisdom-Logo.png'}
          alt="Wisdom Logo"
          style={{ height: '80px', marginBottom: '8px' }}
        />
      </header>
      <h1>Math Slot Machine</h1>
      <div className="reels-container">
        {displayReels.map((reelSymbols, i) => (
          <div key={`reel-${i}-${slotStopped}`} className="reel-wrapper">
             <div 
               className={`reel-inner ${isSpinning ? 'spinning' : 'stopped'}`}
               style={{ 
                 animationDelay: `${i * 0.3}s`,
                 transform: slotStopped ? 'translateY(calc(-80px * 30))' : 'translateY(0)'
               }} 
             >
                <Reel symbols={reelSymbols} symbolType={i === 1 ? 'operator' : 'number'} />
             </div>
          </div>
        ))}
      </div>
      <div className="controls">
        {showAnswerInput ? (
          <div className="answer-input-container">
            <div className="countdown">เวลาที่เหลือ: {countdown} วินาที</div>
            <input
              type="number"
              value={userAnswer}
              onChange={(e) => setUserAnswer(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleSubmit().catch(console.error); // ไม่แสดง feedback ค้าง
                }
              }}
              placeholder="ใส่คำตอบ..."
              className="answer-input"
              autoFocus
              disabled={answerDisabled}
            />
            <button onClick={() => handleSubmit().catch(console.error)} className="submit-button" disabled={answerDisabled}>
              ตอบคำถาม
            </button>
          </div>
        ) : (
          <button onClick={spin} disabled={isSpinning} className="spin-button">
            {isSpinning ? 'กำลังหมุน...' : 'หมุน'}
          </button>
        )}

      </div>
      {/* เพิ่ม audio element ซ่อนไว้ ใช้ path assets ในโฟลเดอร์เดียวกัน */}
      <audio ref={slotOpenAudioRef} src={SlotgameOpen} preload="auto" loop />
      <audio ref={slotTurnAudioRef} src={SlotTurn} preload="auto" />
      <audio ref={countdownAudioRef} src={Countdown3S} preload="auto" />
      {/* ลบเสียงตอบถูก/ผิดออกจาก JSX เพราะใช้ Audio object แทน */}
    </div>
  );
}

// Component หลักที่แสดง SweetAlert
function SlotGameModal({ userData, onUserUpdate, onClose, onOpen }) {
  
  const slotOpenAudioRef = useRef(null);
  const modalShownRef = useRef(false);
  
  // สร้าง audio element สำหรับเล่นเสียง information
  useEffect(() => {
    slotOpenAudioRef.current = new Audio(SlotgameOpen);
    slotOpenAudioRef.current.loop = true;
    slotOpenAudioRef.current.preload = 'auto';
    slotOpenAudioRef.current.volume = 0.7; // ตั้งระดับเสียง
    slotOpenAudioRef.current.muted = false; // เปิดเสียง
    
    // เพิ่ม event listeners สำหรับ error handling
    slotOpenAudioRef.current.addEventListener('error', (e) => console.error('Audio error:', e));
    
    
    return () => {
      if (slotOpenAudioRef.current) {
        slotOpenAudioRef.current.pause();
        slotOpenAudioRef.current.currentTime = 0;
      }
    };
  }, []);
  
  useEffect(() => {
    
    if (userData && onUserUpdate && !modalShownRef.current) {
      
      // ตั้งค่าว่าได้แสดง modal แล้ว
      modalShownRef.current = true;
      
      // ใช้ setTimeout เพื่อให้แน่ใจว่า DOM พร้อม
      setTimeout(() => {
        // ตรวจสอบว่า SweetAlert ยังแสดงอยู่หรือไม่
        if (Swal.isVisible()) {
          return;
        }
        
        
        Swal.fire({
        title: ' Math Slot Machine ',
        html: `
          <div style="text-align: center; padding: 20px;">
            <h3 style="color: #4CAF50; margin-bottom: 20px;">--- Bonus Time ---</h3>
            <div style="margin: 20px 0;">
              <p style="font-weight: bold; color: #FF6B35;">🎯 วิธีเล่น:</p>
              <p style="text-align: left; margin: 10px 0;">1. กดปุ่ม "เริ่มเกม" เพื่อหมุนวงล้อ</p>
              <p style="text-align: left; margin: 10px 0;">2. วงล้อจะหยุดและแสดงโจทย์คณิตศาสตร์</p>
              <p style="text-align: left; margin: 10px 0;">3. ตอบคำถามภายในเวลาที่กำหนด</p>
              <p style="text-align: left; margin: 10px 0;">4. ได้คะแนน 30 คะแนนต่อคำตอบที่ถูกต้อง!</p>
            </div>
            <button id="start-slot-game" style="
              background: linear-gradient(45deg, #4CAF50, #45a049);
              color: white;
              border: none;
              padding: 15px 30px;
              font-size: 18px;
              font-weight: bold;
              border-radius: 25px;
              cursor: pointer;
              box-shadow: 0 4px 8px rgba(0,0,0,0.2);
              transition: all 0.3s ease;
            " onmouseover="this.style.transform='scale(1.05)'" onmouseout="this.style.transform='scale(1)'">
              🎮 เริ่มเกม
            </button>
          </div>
        `,
        width: '500px',
        showConfirmButton: false,
        showCloseButton: false,
        allowOutsideClick: false,
        allowEscapeKey: false,
        backdrop: 'static',
        timer: false,
        timerProgressBar: false,
        didOpen: () => {
          // เรียก onOpen callback ถ้ามี
          if (onOpen) {
            onOpen();
          }
          
          // เล่นเสียง information เมื่อ SweetAlert แสดงขึ้นมา
          if (slotOpenAudioRef.current) {
            slotOpenAudioRef.current.currentTime = 0;
            
            // ลองเล่นเสียงทันที
            slotOpenAudioRef.current.play().catch((error) => {
              console.error('Error playing sound immediately:', error);
              
              // ถ้าเล่นไม่ได้ ให้ลองอีกครั้งหลังจาก user interaction
              const playAfterInteraction = () => {
                slotOpenAudioRef.current.play().catch((retryError) => {
                  console.error('Error playing sound after interaction:', retryError);
                });
                // ลบ event listener หลังจากเล่นแล้ว
                document.removeEventListener('click', playAfterInteraction);
                document.removeEventListener('keydown', playAfterInteraction);
                document.removeEventListener('touchstart', playAfterInteraction);
              };
              
              // เพิ่ม event listeners สำหรับ user interaction
              document.addEventListener('click', playAfterInteraction, { once: true });
              document.addEventListener('keydown', playAfterInteraction, { once: true });
              document.addEventListener('touchstart', playAfterInteraction, { once: true });
            });
          }
          
          // เพิ่ม event listener สำหรับปุ่มเริ่มเกม
          const startButton = document.getElementById('start-slot-game');
          if (startButton) {
            startButton.addEventListener('click', () => {
              // ปิด SweetAlert ปัจจุบัน
              Swal.close();
              
              // แสดง SlotGame ใน SweetAlert ใหม่
              Swal.fire({
                title: '🎰 Math Slot Machine 🎰',
                html: '<div id="slot-game-container"></div>',
                width: '600px',
                showConfirmButton: false,
                showCloseButton: false,
                allowOutsideClick: false,
                allowEscapeKey: false,
                backdrop: 'static',
                didOpen: () => {
                  // แสดง SlotGame ใน SweetAlert
                  const container = document.getElementById('slot-game-container');
                  if (container) {
                    const root = ReactDOM.createRoot(container);
                    root.render(
                      <SlotGame 
                        userData={userData} 
                        onUserUpdate={onUserUpdate}
                        onStopInformationSound={() => {
                          if (slotOpenAudioRef.current) {
                            slotOpenAudioRef.current.pause();
                            slotOpenAudioRef.current.currentTime = 0;
                          }
                        }}
                      />
                    );
                  }
                }
              });
            });
          }
        },
                       willClose: () => {
                 
                 // หยุดเสียงเมื่อปิด modal
                 if (slotOpenAudioRef.current) {
                   slotOpenAudioRef.current.pause();
                   slotOpenAudioRef.current.currentTime = 0;
                 }
                 
                 // รีเซ็ต modal shown ref เพื่อให้สามารถแสดงได้อีก
                 modalShownRef.current = false;
                 
                 // เรียก onClose callback ถ้ามี
                 if (onClose) {
                   onClose();
                 }
               }
      });
      }, 100); // ใช้ setTimeout 100ms
    }
    
    // Cleanup function - ไม่ปิด SweetAlert อัตโนมัติ
    return () => {
      // ไม่ปิด SweetAlert ใน cleanup เพื่อให้หน้า information แสดงค้างไว้
    };
  }, [userData, onUserUpdate, onClose, onOpen]);

  // Component นี้จะไม่แสดงอะไรเลย เพราะจะแสดงเป็น modal แทน
  return null;
}

export default SlotGameModal; 